import { GridColDef } from "@mui/x-data-grid-pro";
import {
  DeleteIcon,
  Edit1Icon,
  Grid2,
  IconButton,
  KebabMenu2Icon,
  PopConfirm,
  Popover,
  Typography,
} from "components";
import GKClientDataGrid from "components/GKClientDataGrid";
import { Show } from "components/Show";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField, matchDate, refetchQuery } from "utils/helpers";
import { moneyCommaSeparator } from "utils/MathFunction";

interface Props {
  setOpenSubModal: (open: boolean) => void;
  setHighWatermarkData: any;
}

const HighWaterMarkHistory = (props: Props) => {
  const { setOpenSubModal, setHighWatermarkData } = props;

  const { id } = useParams();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [confirmationModal, setConfirmationModal] =
    useState<HTMLElement | null>(null);
  const [highWatermarkDetails, setHighWatermarkDetails] = useState(null);

  const { data, isFetching } = useFetchQuery({
    key: ["GET_HIGH_WATERMARK_HISTORY_BY_PORTFOLIO_ID"],
    route: "/api/revenue/high-watermark/",
    requestBody: { portfolio: id },
  });

  const deleteHighWatermark = useMutation({
    mutationKey: ["ADD_HIGH_WATERMARK"],
    mutationFn: (rowId) =>
      axiosInstance.delete(
        `/api/revenue/high-watermark/${rowId}/?portfolio=${id}`,
      ),
    onSuccess: () => {
      refetchQuery("GET_HIGH_WATERMARK_HISTORY_BY_PORTFOLIO_ID");
    },
  });

  const open = Boolean(anchorEl);
  const popoverId = open ? "simple-popover" : undefined;

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Date", "date", 100, 1),
      valueFormatter: (value) => matchDate(value?.value),
    },
    {
      ...extraColumnsField("Amount (₹)", "highWatermark", 100, 1),
      valueGetter: (row) => row?.row?.highWatermark,
      valueFormatter: (params) => moneyCommaSeparator(params.value || 0),
      type: "number",
    },
    {
      ...extraColumnsField("Mode of updation", "generateType", 170, 1),
      valueGetter: (row) => row?.row?.generateType || "-",
    },
    {
      ...extraColumnsField("Update with Invoice", "invoice", 170, 1),
      valueGetter: (row) => row?.row?.invoice || "-",
    },
    {
      ...extraColumnsField("Actions", "action", 100, 1),
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <IconButton
          disabled={params?.row?.generateType !== "Manual"}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            setHighWatermarkDetails(params?.row);
            setAnchorEl(event.currentTarget);
          }}
        >
          <KebabMenu2Icon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Show.When
        isTrue={data?.data?.data?.length !== 0}
        showNodata
        textContent="No High Watermark History Available"
      >
        <GKClientDataGrid
          loading={isFetching}
          columns={columns}
          rows={data?.data?.data || []}
          name="portfolioHighWaterMark"
          maxHeight={"calc(100vh - 403px)"}
        />
      </Show.When>

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: {
              width: "100px",
              borderRadius: "8px",
              p: "8px 0",
              border: "1px solid var(--neutral-100)",
              bgcolor: "var(--white)",
              boxShadow: "4px 4px 12px 0px rgba(0, 0, 0, 0.25);",
            },
          },
        }}
      >
        <Grid2 container>
          <Grid2
            size={12}
            p={"12px"}
            display={"flex"}
            alignItems={"center"}
            gap={1}
            sx={{ cursor: "pointer" }}
          >
            <Edit1Icon />
            <Typography
              fontSize={12}
              fontWeight={600}
              color="var(--grey-900)"
              onClick={() => {
                setHighWatermarkData(highWatermarkDetails);
                setOpenSubModal(true);
                setAnchorEl(null);
              }}
            >
              Edit
            </Typography>
          </Grid2>
          <Grid2
            size={12}
            p={"12px"}
            display={"flex"}
            alignItems={"center"}
            gap={1}
            color={"var(--warning-700)"}
            sx={{ cursor: "pointer" }}
            onClick={(event) => {
              setConfirmationModal(event.currentTarget);
              setAnchorEl(null);
            }}
          >
            <DeleteIcon />
            <Typography fontSize={12} fontWeight={600}>
              Delete
            </Typography>
          </Grid2>
        </Grid2>
      </Popover>

      <Show.When isTrue={Boolean(confirmationModal)}>
        <PopConfirm
          anchorEl={confirmationModal}
          open={Boolean(confirmationModal)}
          setOpen={setConfirmationModal}
          onSuccess={() => deleteHighWatermark.mutate(highWatermarkDetails.id)}
        />
      </Show.When>
    </>
  );
};

export default HighWaterMarkHistory;
